import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowClockwise } from "@phosphor-icons/react";

/**
 * Unified captcha widget for login/signup.
 * Supports: math | recaptcha_v2 | turnstile (from System Settings).
 *
 * onChange(token|null)
 * onEnabled(boolean)
 */
export default function CaptchaWidget({ onChange, onEnabled, className = "" }) {
  const [cfg, setCfg] = useState(null); // null loading | false disabled | {provider, site_key}

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/public")
      .then(({ data }) => {
        if (cancelled) return;
        const c = data?.captcha;
        if (c?.enabled && c?.provider) {
          setCfg({ provider: c.provider, site_key: c.site_key || "" });
        } else {
          setCfg(false);
        }
      })
      .catch(() => { if (!cancelled) setCfg(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (cfg === null) return;
    const enabled = cfg !== false;
    onEnabled?.(enabled);
    if (!enabled) onChange?.(null);
  }, [cfg, onChange, onEnabled]);

  if (cfg === null || cfg === false) return null;

  if (cfg.provider === "math") {
    return (
      <MathCaptcha
        className={className}
        onChange={onChange}
      />
    );
  }
  if (cfg.provider === "turnstile") {
    return (
      <TurnstileWidget
        className={className}
        siteKey={cfg.site_key}
        onChange={onChange}
      />
    );
  }
  return (
    <GoogleRecaptcha
      className={className}
      siteKey={cfg.site_key}
      onChange={onChange}
    />
  );
}

function MathCaptcha({ onChange, className = "" }) {
  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setAnswer("");
    onChange?.(null);
    try {
      const { data } = await api.get("/settings/public/captcha/challenge");
      setChallenge(data);
    } catch {
      setChallenge(null);
      setError("Could not load captcha. Try again.");
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  useEffect(() => { load(); }, [load]);

  // Expose refresh for resetCaptcha()
  useEffect(() => {
    window.__refreshMathCaptcha = load;
    return () => { delete window.__refreshMathCaptcha; };
  }, [load]);

  const onAnswer = (val) => {
    setAnswer(val);
    if (challenge?.id && val.trim() !== "") {
      onChange?.(`${challenge.id}:${val.trim()}`);
    } else {
      onChange?.(null);
    }
  };

  return (
    <div className={className} data-testid="math-captcha-widget">
      <Label className="text-sm">Solve to continue</Label>
      <div className="mt-1.5 flex items-center gap-2">
        <div className="flex h-11 min-w-[7rem] items-center justify-center rounded-lg border border-border bg-muted/40 px-3 font-mono text-base font-semibold tabular-nums">
          {loading ? "…" : challenge ? `${challenge.question} =` : "—"}
        </div>
        <Input
          data-testid="math-captcha-answer"
          inputMode="numeric"
          value={answer}
          onChange={(e) => onAnswer(e.target.value)}
          placeholder="?"
          className="h-11 w-24 text-center font-mono text-base"
          autoComplete="off"
          disabled={loading || !challenge}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-11 w-11 shrink-0"
          onClick={load}
          disabled={loading}
          title="New challenge"
          data-testid="math-captcha-refresh"
        >
          <ArrowClockwise className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

function GoogleRecaptcha({ siteKey, onChange, className = "" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const renderWidget = useCallback(() => {
    if (!siteKey || !containerRef.current || !window.grecaptcha) return;
    if (widgetIdRef.current != null) return;
    try {
      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onChange?.(token || null),
        "expired-callback": () => onChange?.(null),
        "error-callback": () => onChange?.(null),
      });
    } catch {
      /* already rendered */
    }
  }, [siteKey, onChange]);

  useEffect(() => {
    if (!siteKey) return undefined;
    const scriptId = "google-recaptcha-v2";
    const existing = document.getElementById(scriptId);
    if (window.grecaptcha?.render) {
      renderWidget();
      return undefined;
    }
    window.__onRecaptchaLoad = () => renderWidget();
    if (!existing) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = "https://www.google.com/recaptcha/api.js?onload=__onRecaptchaLoad&render=explicit";
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    } else if (window.grecaptcha) {
      window.grecaptcha.ready(renderWidget);
    }
    return () => { widgetIdRef.current = null; };
  }, [siteKey, renderWidget]);

  return (
    <div className={className} data-testid="recaptcha-widget">
      <div ref={containerRef} />
    </div>
  );
}

function TurnstileWidget({ siteKey, onChange, className = "" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const renderWidget = useCallback(() => {
    if (!siteKey || !containerRef.current || !window.turnstile) return;
    if (widgetIdRef.current != null) return;
    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onChange?.(token || null),
        "expired-callback": () => onChange?.(null),
        "error-callback": () => onChange?.(null),
      });
    } catch {
      /* already rendered */
    }
  }, [siteKey, onChange]);

  useEffect(() => {
    if (!siteKey) return undefined;
    const scriptId = "cf-turnstile";
    const existing = document.getElementById(scriptId);
    if (window.turnstile?.render) {
      renderWidget();
      return undefined;
    }
    window.__onTurnstileLoad = () => renderWidget();
    if (!existing) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__onTurnstileLoad&render=explicit";
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
    return () => { widgetIdRef.current = null; };
  }, [siteKey, renderWidget]);

  return (
    <div className={className} data-testid="turnstile-widget">
      <div ref={containerRef} />
    </div>
  );
}

/** Reset / refresh captcha after a failed login/signup. */
export function resetCaptcha() {
  try {
    if (typeof window.__refreshMathCaptcha === "function") {
      window.__refreshMathCaptcha();
      return;
    }
    if (window.grecaptcha?.reset) window.grecaptcha.reset();
    if (window.turnstile?.reset) window.turnstile.reset();
  } catch {
    /* ignore */
  }
}

// Back-compat alias
export function resetRecaptcha() {
  resetCaptcha();
}
